import React, { useMemo, useRef, useCallback } from "react";
import { connect, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import constants from "../../shared/constants";
import recordingConstants from "../../shared/constants/recordingConstants";
import { ANALYTICS_STORAGE_KEYS, LINK_INFO } from "../../shared/constants/analytics";
import epgConstants from "../../shared/constants/epg";
import routeConstants from "../../shared/constants/routes";
import storageConstants from "../../shared/constants/storage";
import { calculateLookbackHoursLeft, isPastProgram, checkUnifiedAsset } from "../../shared/utils";
import { isItemTypeEpisode, isItemTypeMovie } from "../../shared/utils/content";
import classNames from "classnames";
import {
  checkIsStartOverSupported,
  convertDurationToWidth,
  isGhostChannel,
  isUserSubscribedChannel,
  checkIsLookbackSupported,
} from "../../shared/utils/epg";
import {
  getRecordingCTAs,
  getRecordingInfo,
  setRecording,
  getRecordingSystemType,
  isSportsEvent,
  getTeamRecordingParams,
  getExistingTeamEvent,
  getCPVRRecordingStatus,
  doesCPVREventRecordingExist,
  doesCPVRSeriesRecordingExist,
  isCPVRRecordingInProgress,
  isNotLiveTeamEvent,
} from "../../shared/utils/recordingHelper";
import { setSessionStorage, removeSessionStorage } from "../../shared/utils/sessionStorage";
import { showToastNotification } from "../../App/state/actions";
import ImageButton from "../ImageButton";
import ContextualMenu from "../ContextualMenu";
import { setRecordingAction, setTeamRecordingAction } from "../../pages/RecordingsPage/state/actions";
import { getAutoGeneratedObject, getAutogeneratedEpisodeString } from "../../shared/utils";
import { showModalPopup } from "../../App/state/actions";

import usePlaybackChecks from "../../shared/hooks/usePlaybackChecks";

const { ACTION_KEYS, CONTENT_ITEM_TYPES, PAGE_CONTENT_ITEM_TYPES, MODAL_TYPES, PLAY } = constants;
const { RECORDING_PARAMS, RECORDING_PACKAGES } = recordingConstants;
const { CHANNEL_CELL_WIDTH } = epgConstants;
const { LIVE_PLAYER, MOVIE_DETAIL_PAGE, EPISODE_DETAIL_PAGE } = routeConstants;
const { EPG_SELECTED_CHANNEL_NUMBER } = storageConstants;

const outOfHomeIcon = `${process.env.PUBLIC_URL}/images/Out_of_home.svg`;
const inHomeIcon = `${process.env.PUBLIC_URL}/images/In_Home.svg`;
const restartIcon = `${process.env.PUBLIC_URL}/images/Restart.svg`;
const playIcon = `${process.env.PUBLIC_URL}/images/play-icon.svg`;
const recordEpisodeIcon = `${process.env.PUBLIC_URL}/images/set_recording.svg`;
const recordSeriesIcon = `${process.env.PUBLIC_URL}/images/record_series.svg`;
const infoIcon = `${process.env.PUBLIC_URL}/images/info-icon.svg`;
const eventRecordingExistsIconURI = process.env.PUBLIC_URL + "/images/mini_epg_set.svg";
const seriesRecordingExistsIconURI = process.env.PUBLIC_URL + "/images/Series_Recording_Set.svg";

/**
 * EPG program cell component
 *
 * @component
 */
function ProgramCell({
  rowNumber,
  columnNumber,
  appProvider,
  userProfile,
  subscribedChannels,
  program,
  currentTime,
  epgRange,
  channel,
  isInHome,
  recordingsList,
  setRecordingAction,
  showToastNotification,
  setSelectedAssetRecordingInfo,
  updateRecordingHandler,
  style,
  featureToggles,
  showModalPopup,
}) {
  const regionId = appProvider?.channelMapID;
  const {
    isGuideContextMenuEnabled: isContextMenuEnabled,
    isLookbackEnabled,
    isRecordingEnabled,
    isRestartLiveTVEnabled,
    isFastUnrestrictedPlay,
    isTeamRecordingsEnabled,
  } = featureToggles;

  const history = useHistory();
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();

  const { performPlaybackChecks } = usePlaybackChecks();

  const isOnNow = program?.metadata?.airingStartTime <= currentTime && currentTime < program?.metadata?.airingEndTime;
  const duration =
    Math.min(program?.metadata?.airingEndTime, epgRange.end) -
    Math.max(epgRange.start, program?.metadata?.airingStartTime);
  const cellWidth = convertDurationToWidth(duration);
  const offsetLeft =
    convertDurationToWidth(Math.max(program?.metadata?.airingStartTime, epgRange.start) - epgRange.start) +
    CHANNEL_CELL_WIDTH;
  const contextMenuPlayBtnHref = useRef(null);
  const contextMenuDetailsBtnHref = useRef(null);

  const recordingSystemType = useMemo(
    () => (userProfile?.isLoggedIn ? getRecordingSystemType(userProfile) : null),
    [userProfile]
  );

  /** isMR - type: boolean - check if a login user has Media Room recording package, this constant is crucial for recording functions*/
  const isMR = recordingSystemType === RECORDING_PACKAGES.PACKAGE_NAME.LPVRMediaroom_TP;
  const isUnifiedAsset = checkUnifiedAsset(program.metadata?.extendedMetadata?.dlum);

  const isNew = program?.metadata?.extendedMetadata?.epg?.isNew;
  const isLive = program?.metadata?.extendedMetadata?.epg?.isLive;

  const titleBadge =
    duration >= 15 * 60 * 1000 &&
    (isNew || isLive) &&
    (isLive ? translate("epg_badge_live") : translate("epg_badge_new"));

  const allowUnrestrictedPlay =
    channel?.metadata?.extendedMetadata?.isAllowUnrestrictedPlay == "Y" && isFastUnrestrictedPlay;

  const schedule = useMemo(() => {
    return channel && program
      ? {
          id: program.metadata?.contentId, // If the program has lookback enabled, this id is required for lookback content playback analytics at player conponent, see pages/PlayerPage/index.js programDetailsRef reference logic and triggerAnalyticsCalls function logic
          channel: channel,
          metadata: program.metadata,
        }
      : null;
  }, [channel, program]);

  const isSubscribedChannel = useMemo(() => {
    return isUserSubscribedChannel(channel, subscribedChannels);
  }, [channel, subscribedChannels]);

  // To determine if a cell item has lookback asset, only happens when lookback is enabled
  const hasLookback = useMemo(() => {
    if (isLookbackEnabled && schedule) {
      const lookbackHoursLeft = calculateLookbackHoursLeft(schedule);
      const isLookbackSuported = checkIsLookbackSupported(schedule.metadata, schedule.channel, isInHome);
      if (
        userProfile?.isLoggedIn &&
        isSubscribedChannel &&
        schedule.metadata?.airingEndTime < currentTime &&
        lookbackHoursLeft > 0 &&
        isLookbackSuported
      ) {
        return true;
      }
    }
    return false;
  }, [isLookbackEnabled, schedule, userProfile, isSubscribedChannel, currentTime, isInHome]);

  // To determine if a cell item has already aired and consider in the past
  const isProgramInPast = useMemo(() => {
    if (schedule) {
      if (schedule.metadata?.airingEndTime < currentTime) {
        return true;
      }
    }
    return false;
  }, [schedule, currentTime]);

  const programRecordingInfo = useMemo(() => {
    if (isSubscribedChannel && schedule?.metadata?.isRecordable && recordingsList && isRecordingEnabled) {
      return getRecordingInfo(isMR, schedule, recordingsList, regionId);
    }
    return null;
  }, [schedule, recordingsList, isRecordingEnabled, isMR, regionId, isSubscribedChannel]);

  const existingTeamInfo = getExistingTeamEvent(programRecordingInfo, isTeamRecordingsEnabled);

  let cpvrEventRecordingStatus,
    cpvrSeriesRecordingStatus,
    hasRecording = false,
    hasSeriesRecording = false;
  if (!isMR) {
    cpvrEventRecordingStatus = getCPVRRecordingStatus(programRecordingInfo);
    cpvrSeriesRecordingStatus = getCPVRRecordingStatus(programRecordingInfo, true);
    hasRecording =
      programRecordingInfo?.eventRecordingItem &&
      (doesCPVREventRecordingExist(cpvrEventRecordingStatus) ||
        doesCPVRSeriesRecordingExist(cpvrSeriesRecordingStatus));
    hasSeriesRecording = doesCPVRSeriesRecordingExist(cpvrSeriesRecordingStatus);
  }

  const recordingCTA = useMemo(() => {
    if (programRecordingInfo) {
      const eventRecordingSet = programRecordingInfo.recordingEventScheduledCheck;
      const seriesRecordingSet = programRecordingInfo.recordingSeriesScheduledCheck;
      const seriesRecordingConflicted = programRecordingInfo.recordingSeriesConflictCheck;
      const eventRecordingConflicted = programRecordingInfo.recordingEventConflictCheck;
      const cPVRRecordingItem = programRecordingInfo.eventRecordingItem;
      const isRecordingIconAvailable =
        seriesRecordingSet ||
        seriesRecordingConflicted ||
        eventRecordingSet ||
        eventRecordingConflicted ||
        cPVRRecordingItem
          ? true
          : false;
      /**
       * Using the recording status (set or conflict) to check if the icon needs to be provide or not.
       * Currently we are not showing hollow recording icon.
       */
      if (isRecordingIconAvailable) {
        return getRecordingCTAs(
          isMR,
          programRecordingInfo,
          schedule,
          eventRecordingConflicted,
          eventRecordingSet,
          seriesRecordingConflicted,
          seriesRecordingSet
        );
      }
    }
    return null;
  }, [schedule, programRecordingInfo, isMR]);

  const hasTeamSeriesRecordingExists = useMemo(() => {
    // Team recording check
    const sports = programRecordingInfo?.assetToRecord?.metadata?.extendedMetadata?.dlum?.sports;
    if (isTeamRecordingsEnabled && sports?.homeUmId && sports?.guestUmId) {
      const homeTeamParams = getTeamRecordingParams(sports.homeUmId, programRecordingInfo?.seriesRecordingsList);
      const guestTeamParams = getTeamRecordingParams(sports.guestUmId, programRecordingInfo?.seriesRecordingsList);
      if (existingTeamInfo.length > 0 && (homeTeamParams?.recordingExists || guestTeamParams?.recordingExists)) {
        return true;
      }
    }
    return false;
  }, [programRecordingInfo, isTeamRecordingsEnabled, existingTeamInfo]);

  const recordingButton = useMemo(() => {
    const showTeamSeriesIcon = seriesRecordingExistsIconURI && hasTeamSeriesRecordingExists;

    const showRecordingCtaIcon = recordingCTA?.recordingExists && recordingCTA?.recordingIcon;

    if (!showTeamSeriesIcon && !showRecordingCtaIcon) {
      return null;
    }
    const iconSrc = showTeamSeriesIcon ? seriesRecordingExistsIconURI : recordingCTA?.recordingIcon;
    return (
      <ImageButton
        key="recording-button"
        src={iconSrc}
        className="guide-record-icon"
        // RecordingModal component appearing on guide page has been de-scoped for now and only the recording status can be seen (schedule and conflicted)
        buttonContainerStyles="button-container"
        alt="recording button"
      />
    );
  }, [recordingCTA, hasTeamSeriesRecordingExists]);

  const programCellType = useMemo(() => {
    const programInfo = program?.metadata;
    if (isItemTypeMovie(programInfo)) {
      return CONTENT_ITEM_TYPES.movie;
    } else if (isItemTypeEpisode(programInfo)) {
      return CONTENT_ITEM_TYPES.episode;
    }
    return null;
  }, [program]);

  const detailHref = useMemo(() => {
    const programInfo = program?.metadata;
    const programClickAction = program?.actions?.find(
      (programAction) => programAction?.key?.toLowerCase() === ACTION_KEYS.ON_CLICK
    );
    return isItemTypeMovie(programInfo)
      ? `${MOVIE_DETAIL_PAGE.route}${programClickAction?.uri}/${programInfo.contentId}`
      : isItemTypeEpisode(programInfo)
      ? `${EPISODE_DETAIL_PAGE.route}${programClickAction?.uri}/${programInfo.contentId}`
      : null;
  }, [program]);

  const programCellHref = useMemo(() => {
    const programInfo = program?.metadata;
    const playerHref = `${LIVE_PLAYER.route}/?stationId=${channel.id}&contentType=LIVE`;
    contextMenuDetailsBtnHref.current = detailHref;
    if (
      (((userProfile?.isLoggedIn && isSubscribedChannel && !isGhostChannel(channel)) || allowUnrestrictedPlay) &&
        programInfo.airingStartTime <= currentTime &&
        programInfo.airingEndTime >= currentTime) ||
      hasLookback
    ) {
      contextMenuPlayBtnHref.current = playerHref;
      return "#" + playerHref;
    } else {
      return "#" + detailHref;
    }
  }, [program, channel, currentTime, userProfile, isSubscribedChannel, hasLookback, detailHref, allowUnrestrictedPlay]);

  const indicatorIcons = useMemo(() => {
    const recordingIndicator = program?.metadata?.airingEndTime > currentTime && recordingButton;
    const inHomeOutOfHomeIndicator = program?.metadata?.isNotAvailableOutOfHome &&
      !channel?.metadata?.isNotAvailableOutOfHome && (
        <img key="in-home-icon" src={isInHome ? inHomeIcon : outOfHomeIcon} alt="" />
      );
    const restartIndicator = ((isOnNow &&
      isRestartLiveTVEnabled &&
      checkIsStartOverSupported(program?.metadata, channel, isInHome)) ||
      hasLookback) && <img key="restart-icon" src={restartIcon} alt="" />;

    const displayIcons = [recordingIndicator, inHomeOutOfHomeIndicator, restartIndicator].filter((item) => item);

    // If program duration is 15 minutes or less then show only one icon, otherwise show 2 icons
    if (duration <= 15 * 60 * 1000) {
      return displayIcons.slice(0, 1);
    } else {
      return displayIcons.slice(0, 2);
    }
  }, [
    program,
    channel,
    currentTime,
    isInHome,
    isRestartLiveTVEnabled,
    recordingButton,
    duration,
    isOnNow,
    hasLookback,
  ]);

  /**
   * Schedule a recording and save the response in redux
   *
   * @param {String} typeOfRecording - the recording type (event)
   * @param {Object} recordingInfo - recording information used to set the recording
   */
  const scheduleRecording = useCallback(
    (typeOfRecording, recordingInfo) => {
      if (recordingInfo) {
        setRecording(typeOfRecording, appProvider, recordingInfo.assetToRecord, setRecordingAction, isMR);
      }
    },
    [appProvider, isMR, setRecordingAction]
  );

  const scheduleTeamRecording = useCallback(
    async (teamRecordingsParams) => {
      dispatch(setTeamRecordingAction(appProvider, teamRecordingsParams));
    },
    [appProvider, dispatch]
  );

  const manageChannelObj = useCallback(() => {
    if (allowUnrestrictedPlay && !userProfile?.isLoggedIn) {
      setSessionStorage(storageConstants.CHANNEL_OBJ, channel);
    } else {
      removeSessionStorage(storageConstants.CHANNEL_OBJ);
    }
  }, [allowUnrestrictedPlay, channel, userProfile]);

  const onProgramCellClick = (event) => {
    if (!isUnifiedAsset) {
      event.preventDefault();
      showModalPopup(MODAL_TYPES.ERROR, { message: "error_non_unified", isCloseable: true }); // showing error for non-UA assets
    } else if (
      userProfile.isLoggedIn &&
      isSubscribedChannel &&
      (channel?.metadata?.isNotAvailableOutOfHome || program?.metadata?.isNotAvailableOutOfHome) &&
      program.metadata.airingStartTime <= currentTime &&
      program.metadata.airingEndTime >= currentTime &&
      !isInHome
    ) {
      event.preventDefault();
      history.push(detailHref);
    } else if (program.metadata?.isGeoBlocked) {
      event.preventDefault();
      showToastNotification(translate("error_program_not_available_region"));
    } else {
      if (event.currentTarget.href) {
        if (event.currentTarget.href.includes(LIVE_PLAYER.route)) {
          const unrestrictedPlay =
            channel?.metadata?.extendedMetadata?.isAllowUnrestrictedPlay == "Y" && isFastUnrestrictedPlay;
          event.preventDefault();
          performPlaybackChecks(
            null,
            null,
            channel?.id,
            PAGE_CONTENT_ITEM_TYPES.live,
            LIVE_PLAYER.route,
            undefined,
            undefined,
            isSubscribedChannel || unrestrictedPlay ? channel : undefined,
            null,
            hasLookback ? program : null,
            program.metadata.title,
            null,
            { type: "", source: PLAY, unrestrictedPlay: unrestrictedPlay },
            null
          );
        } else {
          manageChannelObj();
        }
        setSessionStorage(ANALYTICS_STORAGE_KEYS.LINK, `${LINK_INFO.GUIDE};${rowNumber}:${columnNumber}`);
        setSessionStorage(EPG_SELECTED_CHANNEL_NUMBER, channel?.number);
      } else {
        event.preventDefault();
        showToastNotification(translate("error_try_again_later"));
      }
    }
  };

  const contextMenuItems = useMemo(() => {
    // if program or channel or schedule is not ready, we don't have data to feed context menu
    if (!program || !channel || !schedule) return null;

    const programInfo = program?.metadata;
    const seriesTitle =
      programRecordingInfo?.assetToRecord?.metadata?.extendedMetadata?.seriesTitle ||
      programRecordingInfo?.assetToRecord?.metadata?.extendedMetadata?.dlum?.sports?.name;
    const menuItems = [];
    // Play button logic
    if (
      (userProfile?.isLoggedIn &&
        isSubscribedChannel &&
        !isGhostChannel(channel) &&
        programInfo?.airingStartTime <= currentTime &&
        programInfo?.airingEndTime >= currentTime &&
        (isInHome ||
          (!isInHome && !(channel?.metadata?.isNotAvailableOutOfHome || programInfo?.isNotAvailableOutOfHome)))) ||
      hasLookback
    ) {
      menuItems.push({
        iconKey: "play-icon",
        iconSrc: playIcon,
        label: translate("play"),
        onClickHandler: () => {
          setSessionStorage(EPG_SELECTED_CHANNEL_NUMBER, channel?.number);
          performPlaybackChecks(
            null,
            null,
            channel?.id,
            PAGE_CONTENT_ITEM_TYPES.live,
            LIVE_PLAYER.route,
            undefined,
            undefined,
            isSubscribedChannel ? channel : undefined,
            null,
            hasLookback ? schedule : null,
            null,
            null,
            { type: "", source: PLAY }
          );
        },
      });
    }
    // Recording buttons logic
    if (programRecordingInfo && !isPastProgram(programInfo?.airingEndTime)) {
      if (schedule.channel && schedule.metadata) {
        // Set/Edit single recording button
        const autoGeneratedObject = getAutoGeneratedObject(programRecordingInfo.assetToRecord?.metadata);
        const episodeInfo = getAutogeneratedEpisodeString(
          autoGeneratedObject,
          programRecordingInfo.assetToRecord?.metadata ?? programRecordingInfo,
          false
        );
        let inProgressRecordingEvent = isCPVRRecordingInProgress(programRecordingInfo?.eventRecordingItem);
        if (
          inProgressRecordingEvent ||
          existingTeamInfo?.length > 0 ||
          hasRecording ||
          recordingCTA?.actionType === RECORDING_PARAMS.EDIT_REC ||
          recordingCTA?.actionType === RECORDING_PARAMS.CANCEL_REC
        ) {
          const editLabel = translate(RECORDING_PARAMS.EDIT);
          let labelText;

          if (isMR) {
            labelText = editLabel;
          } else if (isSportsEvent(programRecordingInfo?.assetToRecord)) {
            labelText = inProgressRecordingEvent ? translate("cancel_event") : translate("edit_event");
          } else if (episodeInfo) {
            labelText = inProgressRecordingEvent
              ? `${translate(RECORDING_PARAMS.CANCEL)} ${episodeInfo}`
              : `${editLabel} ${episodeInfo}`;
          } else {
            labelText = inProgressRecordingEvent ? translate("cancel_rec") : editLabel;
          }
          menuItems.push({
            iconKey: "edit-single-recording-icon",
            iconSrc: eventRecordingExistsIconURI,
            label: labelText,
            onClickHandler: (event) => {
              event.stopPropagation();
              updateRecordingHandler(
                programRecordingInfo,
                existingTeamInfo?.length > 0 ? RECORDING_PARAMS.TEAM_EVENT : RECORDING_PARAMS.EVENT
              );
            },
          });
        } else if (programCellType === CONTENT_ITEM_TYPES.movie || programCellType === CONTENT_ITEM_TYPES.episode) {
          const labelText = isSportsEvent(programRecordingInfo?.assetToRecord)
            ? translate("record_event")
            : episodeInfo
            ? `${translate(RECORDING_PARAMS.RECORD)} ${episodeInfo}`
            : translate(RECORDING_PARAMS.RECORD);
          menuItems.push({
            iconKey: "set-single-recording-icon",
            iconSrc: recordEpisodeIcon,
            label: labelText,
            onClickHandler: (event) => {
              event.stopPropagation();
              setSelectedAssetRecordingInfo(programRecordingInfo);
              scheduleRecording(RECORDING_PARAMS.EVENT, programRecordingInfo);
            },
          });
        }
        // Set/Edit series recording button
        // Note: for MR recording, recordingCTA is returned as RECORDING_PARAMS.EDIT_REC no matter the recording is series or event, so we need to check the actionType for RECORDING_PARAMS.EDIT_REC, too
        if (recordingCTA?.actionType !== RECORDING_PARAMS.EDIT_REC && hasSeriesRecording) {
          menuItems.push({
            iconKey: "edit-series-recording-icon",
            iconSrc: seriesRecordingExistsIconURI,
            label: isSportsEvent(programRecordingInfo?.assetToRecord)
              ? translate("edit_series_title").replace("%s", seriesTitle)
              : translate(RECORDING_PARAMS.EDIT_SERIES),
            onClickHandler: (event) => {
              event.stopPropagation();
              updateRecordingHandler(programRecordingInfo, RECORDING_PARAMS.SERIES);
            },
          });
        } else {
          menuItems.push({
            iconKey: "set-series-recording-icon",
            iconSrc: recordSeriesIcon,
            label: isSportsEvent(programRecordingInfo?.assetToRecord)
              ? translate("record_series_title").replace("%s", seriesTitle)
              : translate(RECORDING_PARAMS.RECORD_SERIES),
            onClickHandler: (event) => {
              event.stopPropagation();
              setSelectedAssetRecordingInfo(programRecordingInfo);
              scheduleRecording(RECORDING_PARAMS.SERIES, programRecordingInfo);
            },
          });
        }
      }
    }
    if (
      userProfile?.isLoggedIn &&
      isSubscribedChannel &&
      !isPastProgram(programInfo?.airingEndTime) &&
      !isMR &&
      isTeamRecordingsEnabled &&
      isSportsEvent(program)
    ) {
      const sports = program?.metadata?.extendedMetadata?.dlum?.sports;
      const teams = [
        { umId: sports?.homeUmId, name: sports?.homeAbbreviation || sports?.homeTeam },
        { umId: sports?.guestUmId, name: sports?.guestAbbreviation || sports?.guestTeam },
      ].filter((team) => team.name);

      teams.forEach((team) => {
        const recordingParams = getTeamRecordingParams(team.umId, programRecordingInfo?.seriesRecordingsList);
        const ctaTextKey = recordingParams?.recordingExists
          ? translate("edit_team_title").replace("%s", team.name)
          : translate("record_team_title").replace("%s", team.name);

        menuItems.push({
          iconKey: `${recordingParams?.recordingExists ? "edit-" : "set-"}team-series-recording-icon-${team.umId}`,
          iconSrc: recordingParams?.recordingIcon,
          label: ctaTextKey,
          onClickHandler: (event) => {
            event.stopPropagation();
            if (recordingParams?.recordingExists) {
              updateRecordingHandler({ ...programRecordingInfo, ...recordingParams }, RECORDING_PARAMS.TEAM);
              return;
            }

            const isNotLiveTeamEventRecording = isTeamRecordingsEnabled
              ? isNotLiveTeamEvent(programRecordingInfo?.assetToRecord)
              : false;

            const setTeamRecording = () => {
              setSelectedAssetRecordingInfo(programRecordingInfo);
              scheduleTeamRecording({
                filter: RECORDING_PARAMS.SPORTS_TEAM,
                filterBy: { teamUmId: team.umId },
              });
            };

            if (isNotLiveTeamEventRecording) {
              const autoGeneratedObject = getAutoGeneratedObject(programRecordingInfo?.assetToRecord?.metadata);

              showModalPopup(MODAL_TYPES.ERROR, {
                title: translate("team_recording_first_run"),
                message: translate("team_recording_rerun").replace(
                  "%s",
                  getAutogeneratedEpisodeString(autoGeneratedObject, programRecordingInfo?.assetToRecord?.metadata)
                ),
                isCloseable: true,
                closeErrorModal: true,
                className: "team-rerun-modal",
                endButtonClickHandlerOverride: setTeamRecording,
              });

              return;
            }

            setTeamRecording();
          },
        });
      });
    }
    // detail button
    menuItems.push({
      iconKey: "details-icon",
      iconSrc: infoIcon,
      label: translate("details"),
      onClickHandler: () => {
        setSessionStorage(EPG_SELECTED_CHANNEL_NUMBER, channel?.number);
        manageChannelObj();

        if (!isUnifiedAsset) {
          showModalPopup(MODAL_TYPES.ERROR, { message: "error_non_unified", isCloseable: true }); // showing error for non-UA assets
        } else {
          history.push(contextMenuDetailsBtnHref.current);
        }
      },
    });

    return menuItems;
  }, [
    program,
    channel,
    schedule,
    userProfile,
    isSubscribedChannel,
    currentTime,
    isInHome,
    hasLookback,
    programRecordingInfo,
    translate,
    performPlaybackChecks,
    recordingCTA,
    programCellType,
    setSelectedAssetRecordingInfo,
    scheduleRecording,
    isMR,
    updateRecordingHandler,
    history,
    isUnifiedAsset,
    showModalPopup,
    manageChannelObj,
    isTeamRecordingsEnabled,
    scheduleTeamRecording,
    existingTeamInfo?.length,
    hasRecording,
    hasSeriesRecording,
  ]);

  return program ? (
    isContextMenuEnabled ? (
      <>
        <ContextualMenu
          id={`guide-context-menu-${program?.id}`}
          contextMenuItems={contextMenuItems}
          menuAppendTo=".epg-container"
        >
          <a
            href={programCellHref}
            className={classNames(
              "cell",
              "program-cell",
              { "on-now": isOnNow && (userProfile?.isLoggedIn || allowUnrestrictedPlay) },
              { lookback: hasLookback }
            )}
            data={`${
              isOnNow ? "on-now-" : hasLookback ? "lookback-" : isProgramInPast ? "aired-date-past-" : "future-"
            }channel-id-${channel.id}`}
            style={{
              ...style,
              width: cellWidth,
              left: offsetLeft,
            }}
            onClick={onProgramCellClick}
          >
            <div className="program-name">
              <div className="sticky-header">
                <span className="title-badge">{titleBadge}</span>
                <span>{program?.metadata?.title}</span>
              </div>
            </div>
            {indicatorIcons.length > 0 ? <div className="icon-container">{indicatorIcons}</div> : null}
          </a>
        </ContextualMenu>
      </>
    ) : (
      <a
        href={programCellHref}
        className={classNames("cell", "program-cell", {
          "on-now": isOnNow && userProfile?.isLoggedIn,
        })}
        data={`${
          isOnNow ? "on-now-" : hasLookback ? "lookback-" : isProgramInPast ? "aired-date-past-" : "future-"
        }channel-id-${channel.id}`}
        style={{
          ...style,
          width: cellWidth,
          left: offsetLeft,
        }}
        onClick={onProgramCellClick}
      >
        <div className="program-name">
          <div className="sticky-header">
            <span className="title-badge">{titleBadge}</span>
            <span>{program?.metadata?.title}</span>
          </div>
        </div>
        {indicatorIcons.length > 0 ? <div className="icon-container">{indicatorIcons}</div> : null}
      </a>
    )
  ) : null;
}

ProgramCell.propTypes = {
  rowNumber: PropTypes.number,
  columnNumber: PropTypes.number,
  program: PropTypes.object,
  currentTime: PropTypes.number,
  epgRange: PropTypes.object,
  channel: PropTypes.object,
  recordingSystemType: PropTypes.string,
  isRecordingEnabled: PropTypes.bool,
  isRestartLiveTVEnabled: PropTypes.bool,
  style: PropTypes.object,
};

function mapStateToProps({ app, recording }) {
  return {
    appProvider: app.provider,
    userProfile: app.userProfile,
    isInHome: app.isInHome,
    subscribedChannels: app.subscribedChannels,
    recordingsList: recording.recordingsList,
    favouriteAssets: app.favouriteAssets,
    featureToggles: app.featureToggles,
  };
}

const mapDispatchToProps = {
  setRecordingAction,
  showToastNotification,
  showModalPopup,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProgramCell);
